"use client";
import Image from "next/image";
import { Fragment, useEffect, useState } from "react";
import dynamic from "next/dynamic";

import Banner from "./limited-offer-banner";
import NavBar from "./nav-bar";
import { items } from "@/lib/data";

const PilotModal = dynamic(() => import("./pilot-modal"), {
  loading: () => null,
  ssr: false,
});

export const HeroSection = () => {
  const [isPilotModalOpen, setIsPilotModalOpen] = useState(false);

  return (
    <>
      <section className="" id="home">
        <div>
          <Banner />
          <NavBar />
        </div>

        <div className="2xl:container 2xl:mx-auto 2xl:px-4 sm:px-4">
          <div className="relative overflow-hidden bg-surface-white/5">
            {/* Background image - Optimised for LCP */}
            <Image
              src="/images/hero-section/hero-bg-default.webp"
              alt="background image"
              fill
              priority
              fetchPriority="high"
              className="object-cover animate-hero-zoom"
            />

            {/* Overlay */}
            <div className="absolute inset-0 z-1 bg-black/25 backdrop-blur-[6px] sm:bg-transparent sm:backdrop-blur-none pointer-events-none transition-all duration-500" />

            {/* Content */}
            <div className="relative z-10 text-surface-white h-fit">
              {/* Badge */}
              <div className="absolute left-5 sm:left-12 md:left-8 lg:left-16 xl:left-19.75 top-4 lg:top-5 w-fit h-fit bg-black/25 md:bg-primary/20 backdrop-blur-[8px] md:backdrop-blur-none text-surface-white md:text-surface shadow-[0_4px_24px_rgba(0,0,0,0.1)] md:shadow-none md:drop-shadow-[0_0_24px_rgba(226,226,226,0.8)] ring-1 ring-white/20 md:ring-0 rounded-[30px] py-2.5 lg:py-2.5 xl:py-4 px-2.5 lg:px-3 xl:px-4 flex items-center gap-x-1 font-medium z-10 text-sm xl:text-base opacity-0 animate-hero-fade-in-up">
                <span className="bg-primary rounded-full size-1.5 lg:size-2 xl:size-2.5 animate-pulse" />
                <span>Maternal support, reimagined</span>
              </div>

              {/* Main content */}
              <div className="px-4 sm:px-6 lg:px-10 xl:px-14.75 pt-26 sm:pt-34 lg:pt-49 xl:pt-65.25">
                <div className="text-center space-y-4 lg:space-y-6 xl:space-y-8 font-semibold">
                  <div className="space-y-2.5 lg:space-y-4 xl:space-y-6">
                    <div className="overflow-hidden">
                      <h1
                        className="leading-9 sm:leading-9 lg:leading-13 xl:leading-18 text-[26px] sm:text-[34px] lg:text-[42px] xl:text-[60px] tracking-normal animate-hero-slide-up"
                        style={{ animationDelay: "400ms" }}
                      >
                        Pregnancy & postpartum support{" "}
                        <br className="hidden md:block" /> that feels safe and
                        connected
                      </h1>
                    </div>
                    <p
                      className="leading-[1.7] sm:leading-normal text-base lg:text-lg xl:text-xl tracking-normal max-w-[500px] lg:max-w-[650px] xl:max-w-[800px] mx-auto font-medium opacity-0 animate-hero-fade-in-up"
                      style={{ animationDelay: "800ms" }}
                    >
                      BrimmHQ helps women navigate pregnancy and early
                      motherhood with trusted support they need - all in one
                      place
                    </p>
                  </div>

                  <div
                    className="mt-5 sm:mt-0 [&_button]:w-full [&_button]:sm:w-fit flex flex-col sm:flex-row items-center justify-center gap-y-3 gap-x-3 sm:gap-x-2.5 lg:gap-x-4 xl:gap-x-6 [&_button]:px-4.5 [&_button]:sm:px-3.5 [&_button]:lg:px-4.5 [&_button]:xl:px-6 [&_button]:py-3.5 [&_button]:sm:py-2.5 [&_button]:lg:py-4 [&_button]:xl:py-4.5 [&_button]:rounded-[50px] [&_button]:cursor-pointer text-sm xl:text-base opacity-0 animate-hero-scale-in"
                    style={{ animationDelay: "1200ms" }}
                  >
                    <button
                      className="bg-primary shadow-[0px_0px_25px_0px_#0000001F]!"
                      onClick={() => setIsPilotModalOpen(true)}
                    >
                      Join the Pilot
                    </button>
                    <button
                      className="border-2 border-primary drop-shadow-[1px_1px_24px_rgba(0,0,0,0.12)]"
                      onClick={() => {
                        const element = document.getElementById("how-it-works");
                        if (element) {
                          const headerOffset = 100;
                          const elementPosition =
                            element.getBoundingClientRect().top;
                          const offsetPosition =
                            elementPosition + window.scrollY - headerOffset;
                          window.scrollTo({
                            top: offsetPosition,
                            behavior: "smooth",
                          });
                        }
                      }}
                    >
                      Learn How It Works
                    </button>
                  </div>
                </div>

                {/* Stats row */}
                <div
                  className="md:px-4 lg:px-8 xl:px-15.25 pb-5 lg:pb-7.5 xl:pb-8.75 opacity-0 animate-hero-fade-in-up"
                  style={{ animationDelay: "1500ms" }}
                >
                  <div className="mt-14 sm:mt-28 lg:mt-44 xl:mt-63 flex flex-col w-fit mx-auto sm:w-full sm:flex-row sm:items-stretch sm:justify-between gap-y-5 sm:gap-6 lg:gap-8 xl:gap-x-8 ">
                    {items.map((item, index) => (
                      <Fragment key={item.id}>
                        <div
                          className="flex flex-row items-center text-left sm:flex-col sm:text-center lg:flex-row lg:text-left gap-4 sm:gap-2.5 lg:gap-3.5 xl:gap-4 opacity-0 animate-stats-entry"
                          style={{ animationDelay: `${1700 + index * 300}ms` }}
                        >
                          <div
                            className={`relative size-12 shrink-0 sm:size-14 lg:size-16 xl:size-20 rounded-full ${item.bgColor} drop-shadow-[0_4px_24px_rgba(0,0,0,0.08)]`}
                          >
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                              <div className="relative shrink-0 size-4.5 sm:size-4.5 lg:size-5 xl:size-6">
                                <Image
                                  src={item.icon}
                                  fill
                                  alt={item.subtext}
                                  className="object-cover"
                                />
                              </div>
                            </div>
                            <div
                              className={`absolute size-8 sm:size-8.5 lg:size-10.5 xl:size-12.5 z-5 left-1/2 drop-shadow-[0_0_24px_rgba(226,226,226,0.7)] rounded-full top-1/2 -translate-x-1/2 -translate-y-1/2 ${item.innerBg}`}
                            />
                          </div>

                          <div className="space-y-1 xl:space-y-2 tracking-normal xl:min-w-61.5">
                            <p className="font-bold text-base lg:text-lg xl:text-2xl text-surface-muted sm:text-surface leading-none">
                              {item.text}
                            </p>
                            <span className="font-medium text-sm xl:text-base text-surface-muted sm:text-surface/70">
                              {item.subtext}
                            </span>
                          </div>
                        </div>

                        {index !== items.length - 1 && (
                          <div className="hidden sm:grid items-center self-stretch">
                            <div className="w-0.5 bg-surface-white h-1/2 lg:h-full" />
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PilotModal
        isOpen={isPilotModalOpen}
        onClose={() => setIsPilotModalOpen(false)}
      />
    </>
  );
};
